/**
 * Deleting a saved report, behind a plain-words confirmation.
 *
 * A system report cannot be deleted (the server refuses it); the control says
 * so up front instead of letting someone try.
 */
import { useState } from 'react'
import { Trash2 } from 'lucide-react'
import type { LibraryReport } from './library'
import { Btn } from './ui'
import type { ReportActions } from './useReportActions'
import type { ReportPermissions } from './useReportingData'

export function DeleteReport({
  report,
  actions,
  perms,
  onDeleted,
}: {
  report: LibraryReport
  actions: ReportActions
  perms: ReportPermissions
  onDeleted?: () => void
}) {
  // The confirmation names the report it was opened for. The panel is reused as the selection moves, so a
  // bare flag would carry an open "Delete?" from one report to the next and delete the one now selected.
  const [confirmingId, setConfirmingId] = useState<string | null>(null)
  const definition = report.definition
  if (!definition) return null
  const confirming = confirmingId === definition.id

  return (
    <>
      <Btn
        size="sm"
        variant="quiet"
        icon={<Trash2 className="h-3.5 w-3.5" aria-hidden />}
        disabled={!perms.canEdit || definition.isSystem}
        title={
          definition.isSystem
            ? 'A system report cannot be deleted. Duplicate it instead.'
            : perms.canEdit
              ? undefined
              : 'Your role can view and run reports but not change them.'
        }
        onClick={() => setConfirmingId(definition.id)}
        aria-label={`Delete ${report.name}`}
      >
        Delete
      </Btn>
      {confirming ? (
        <div
          role="alertdialog"
          aria-label={`Delete ${report.name}?`}
          className="basis-full rounded-lg border border-[#4A3220] bg-[#140E0A] p-3 text-[12.5px] text-[#E8D3C4]"
        >
          <p>Delete “{report.name}”? Its versions go with it. Files it already made stay in the archive.</p>
          <div className="mt-2 flex gap-2">
            <Btn
              size="sm"
              busy={actions.remove.isPending}
              onClick={() =>
                actions.remove.mutate(definition.id, {
                  onSuccess: () => onDeleted?.(),
                  onSettled: () => setConfirmingId(null),
                })
              }
            >
              Yes, delete
            </Btn>
            <Btn size="sm" variant="quiet" onClick={() => setConfirmingId(null)}>
              Keep it
            </Btn>
          </div>
        </div>
      ) : null}
    </>
  )
}
